// client/src/components/ChangePassword.tsx
import React, { useState } from "react";
import { Lock, Eye, EyeOff, Loader2, CheckCircle, XCircle } from "lucide-react";
import { settingsService } from "@/api/services/settingsService";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { getErrorMessage } from "@/utils/errorUtils";

export default function ChangePassword() {
  const { t } = useTranslation();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = t("operator.settingsPage.changePassword.validation.currentRequired", { defaultValue: "Current password is required" });
    }

    if (!formData.newPassword) {
      newErrors.newPassword = t("operator.settingsPage.changePassword.validation.newRequired", { defaultValue: "New password is required" });
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = t("operator.settingsPage.changePassword.validation.minLength", { defaultValue: "Password must be at least 8 characters" });
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t("operator.settingsPage.changePassword.validation.confirmRequired", { defaultValue: "Please confirm your password" });
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t("operator.settingsPage.changePassword.validation.noMatch", { defaultValue: "Passwords do not match" });
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = t("operator.settingsPage.changePassword.validation.mustDiffer", { defaultValue: "New password must be different from current password" });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      await settingsService.changePassword(formData);
      setMessage({ type: "success", text: t("operator.settingsPage.changePassword.messages.success", { defaultValue: "Password changed successfully!" }) });
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error: unknown) {
      const axiosErr = error instanceof AxiosError
        ? error as AxiosError<{ code?: string; error?: string; message?: string }>
        : null;
      setMessage({
        type: "error",
        text:
          axiosErr?.response?.data?.code === "PASSWORD_REUSED"
            ? t("operator.settingsPage.changePassword.messages.reused", { defaultValue: "This password has already been used. Please choose a different one." })
            : axiosErr?.response?.data?.error || axiosErr?.response?.data?.message || getErrorMessage(error) || t("operator.settingsPage.changePassword.messages.failed", { defaultValue: "Failed to change password" }),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl dark:bg-[#333436] p-8">
      <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-1">{t("operator.settingsPage.changePassword.title", { defaultValue: "Change Password" })}</h3>
      <p className="text-xs text-gray-500 dark:text-gray-300 mb-6">
        {t("operator.settingsPage.changePassword.subtitle", { defaultValue: "Update your password to keep your account secure" })}
      </p>

      {message && (
        <div
          className={`mb-4 p-4 rounded-lg flex items-center gap-2 ${
            message.type === "success"
              ? "bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400"
              : "bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400"
          }`}
        >
          {message.type === "success" ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <XCircle className="w-5 h-5" />
          )}
          <span className="text-sm">{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Current Password */}
        <div>
          <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1.5">
            {t("operator.settingsPage.changePassword.currentPassword", { defaultValue: "Current Password" })}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type={showCurrentPassword ? "text" : "password"}
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              placeholder={t("operator.settingsPage.changePassword.placeholders.current", { defaultValue: "Enter current password" })}
              className={`w-full pl-10 pr-12 py-3 bg-gray-100 dark:bg-[#1a1b1e] rounded-md text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                errors.currentPassword
                  ? "focus:ring-red-500 border border-red-500"
                  : "focus:ring-gray-800 dark:focus:ring-blue-600"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.currentPassword && (
            <p className="mt-1 text-xs text-red-500">{errors.currentPassword}</p>
          )}
        </div>

        {/* New Password */}
        <div>
          <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1.5">
            {t("operator.settingsPage.changePassword.newPassword", { defaultValue: "New Password" })}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type={showNewPassword ? "text" : "password"}
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder={t("operator.settingsPage.changePassword.placeholders.new", { defaultValue: "Enter new password" })}
              className={`w-full pl-10 pr-12 py-3 bg-gray-100 dark:bg-[#1a1b1e] rounded-md text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                errors.newPassword
                  ? "focus:ring-red-500 border border-red-500"
                  : "focus:ring-gray-800 dark:focus:ring-blue-600"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.newPassword && (
            <p className="mt-1 text-xs text-red-500">{errors.newPassword}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1.5">
            {t("operator.settingsPage.changePassword.confirmNewPassword", { defaultValue: "Confirm New Password" })}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type={showConfirmPassword ? "text" : "password"}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder={t("operator.settingsPage.changePassword.placeholders.confirm", { defaultValue: "Confirm new password" })}
              className={`w-full pl-10 pr-12 py-3 bg-gray-100 dark:bg-[#1a1b1e] rounded-md text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 ${
                errors.confirmPassword
                  ? "focus:ring-red-500 border border-red-500"
                  : "focus:ring-gray-800 dark:focus:ring-blue-600"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full px-6 py-3 bg-gray-900 text-white dark:bg-blue-700 text-sm rounded-md hover:bg-gray-800 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t("operator.settingsPage.changePassword.actions.changing", { defaultValue: "Changing Password..." })}
            </>
          ) : (
            t("operator.settingsPage.changePassword.actions.change", { defaultValue: "Change Password" })
          )}
        </button>
      </form>
    </div>
  );
}
